export type ClientLogLevel = 'debug' | 'info' | 'warn' | 'error'

/**
 * Произвольный контекст записи: комната, браузер, счётчики ICE и тому подобное.
 *
 * Сюда нельзя класть токены и секреты — строка уйдёт в Loki как есть.
 */
export type ClientLogContext = Record<string, unknown>

/**
 * Одна клиентская запись до отправки. timestamp — миллисекунды Date.now();
 * наносекунды для Loki считает уже API-роут.
 */
export type ClientLogEntry = {
  level: ClientLogLevel
  message: string
  context?: ClientLogContext
  timestamp: number
}

const MAX_BATCH_SIZE = 50
const FLUSH_DELAY_MS = 3000

let buffer: ClientLogEntry[] = []
let flushTimer: ReturnType<typeof setTimeout> | null = null
let globalHandlersBound = false

/**
 * Проверяет, что код исполняется в браузере, а не на сервере Next.js.
 */
function isBrowser(): boolean {
  return typeof window !== 'undefined'
}

/**
 * Копирует контекст через JSON, чтобы отсечь циклы и несериализуемые поля.
 *
 * Иначе fetch упадёт на stringify, и вся пачка логов потеряется.
 */
function toSafeContext(context?: ClientLogContext | object): ClientLogContext | undefined {
  if (context == null || typeof context !== 'object') {
    return undefined
  }

  try {
    return JSON.parse(JSON.stringify(context)) as ClientLogContext
  } catch {
    return undefined
  }
}

/**
 * Отправляет пачку на `/api/client_logs`. Ошибки сети глотаем: логи не должны
 * ломать встречу.
 *
 * keepalive только при закрытии вкладки: у keepalive-fetch лимит тела около
 * 64 КБ, и периодический flush с 50 записями в него не влезает.
 *
 * @param keepalive - true лишь для pagehide/beforeunload, иначе браузер
 *   может молча отбросить пачку
 */
function sendEntries(entries: ClientLogEntry[], keepalive: boolean): void {
  if (entries.length === 0) {
    return
  }

  void fetch('/api/client_logs', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ entries }),
    keepalive,
  }).catch(() => undefined)
}

/**
 * Сбрасывает буфер сразу, не дожидаясь таймера.
 *
 * @param keepalive - true, когда вкладку закрывают и обычный fetch отменят
 */
export function flushClientLogs(keepalive = false): void {
  if (flushTimer != null) {
    clearTimeout(flushTimer)
    flushTimer = null
  }

  if (buffer.length === 0) {
    return
  }

  const entries = buffer
  buffer = []

  sendEntries(entries, keepalive)
}

/**
 * Ставит отложенную отправку, если таймер ещё не тикает.
 */
function scheduleFlush(): void {
  if (flushTimer != null) {
    return
  }

  flushTimer = setTimeout(() => {
    flushTimer = null
    flushClientLogs()
  }, FLUSH_DELAY_MS)
}

/**
 * Подписывается на глобальные ошибки один раз на страницу.
 *
 * Раньше это делал Datadog через `forwardErrorsToLogs`. Флаг нужен потому,
 * что `clientLog` импортируют из многих файлов, и без него навесилось бы
 * несколько одинаковых слушателей.
 */
function registerGlobalErrorHandlers(): void {
  if (!isBrowser() || globalHandlersBound) {
    return
  }

  globalHandlersBound = true

  const previousOnError = window.onerror

  window.onerror = (message, source, lineno, colno, error) => {
    const text =
      error instanceof Error
        ? error.message
        : typeof message === 'string'
          ? message
          : 'window.onerror'

    clientLogError(text, {
      source,
      lineno,
      colno,
    })

    if (typeof previousOnError === 'function') {
      return previousOnError.call(window, message, source, lineno, colno, error)
    }

    return false
  }

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason
    const text =
      reason instanceof Error ? reason.message : String(reason ?? 'unhandledrejection')

    clientLogError(text, { type: 'unhandledrejection' })
  })

  window.addEventListener('pagehide', () => {
    flushClientLogs(true)
  })

  window.addEventListener('beforeunload', () => {
    flushClientLogs(true)
  })
}

/**
 * Кладёт запись в буфер и отправляет пачку по заполнению или по таймеру.
 *
 * На сервере ничего не делает: window там нет, а логи встречи нужны именно с клиента.
 */
function clientLog(
  level: ClientLogLevel,
  message: string,
  context?: ClientLogContext | object,
): void {
  if (!isBrowser()) {
    return
  }

  try {
    registerGlobalErrorHandlers()

    if (typeof message !== 'string' || message.trim() === '') {
      return
    }

    buffer.push({
      level,
      message,
      context: toSafeContext(context),
      timestamp: Date.now(),
    })

    if (buffer.length >= MAX_BATCH_SIZE) {
      flushClientLogs()
    } else {
      scheduleFlush()
    }
  } catch {
    // Логи не имеют права ронять интерфейс встречи
  }
}

/**
 * Пишет отладочную запись. LiveKit шлёт их очень часто — лишнее отрежет API.
 */
export function clientLogDebug(message: string, context?: ClientLogContext | object): void {
  clientLog('debug', message, context)
}

/**
 * Пишет информационную запись о ходе подключения.
 */
export function clientLogInfo(message: string, context?: ClientLogContext | object): void {
  clientLog('info', message, context)
}

/**
 * Пишет предупреждение: сеть ещё жива, но уже ведёт себя подозрительно.
 */
export function clientLogWarn(message: string, context?: ClientLogContext | object): void {
  clientLog('warn', message, context)
}

/**
 * Пишет ошибку, из‑за которой участник, скорее всего, не попадёт во встречу.
 */
export function clientLogError(message: string, context?: ClientLogContext | object): void {
  clientLog('error', message, context)
}

if (isBrowser()) {
  registerGlobalErrorHandlers()
}
